import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { cloneDeep, isEqual, orderBy } from 'lodash'
import lang from '@/lang'

import MultiSelectInput, { ACTION_TYPES } from '@/modules/base/components/inputs/multi-select-input/multi-select-input'
import theme from '@/theme'
import { REMOVABLE_SYSTEM_TAGS, TAG_TYPES } from '@/modules/file/constants'
import { isAssignableSystemTag } from '@/modules/base/helpers/isAssignableSystemTag'
import { TagService } from '@/modules/tags/tagService'

export const TAG_CATEGORY = Object.freeze({
  ACTIVITY: 'activityCategories',
  ARTICLEUNITS: 'articleUnits',
  ARTICLECATEGORIES: 'articleCategories',
  CUSTOMERCATEGORIES: 'customerCategories',
  CUSTOM: 'custom',
  CUSTOMERS: 'customers',
  DOCUMENTS: 'documents',
  EXPENSES: 'expenses',
  JOBTITLES: 'jobTitles',
  REVENUES: 'revenues',
  SALUTATIONS: 'salutations',
  SYSTEM: 'system',
  TITLES: 'titles',
  SUPPLIERS: 'suppliers',
  SUPPLIERALIASES: 'supplierAliases',
  ARTICLECODES: 'articleCodes',
  INVOICES: 'invoices',
  ORDERS: 'orders',
  OFFERS: 'offers',
  DELIVERYNOTES: 'deliveryNotes',
  TIMETRACKINGHOURLYRATES: 'timeTrackingHourlyRates',
})

export const cache = new Map()

export const loadTags = (category) => {
  return new Promise((resolve, reject) => {
    TagService.fetchTags(category).then(response => {
      const { body: { data: options } } = response
      if (!isEqual(options, cache.get(category))) {
        cache.set(category, orderBy(options, 'label'))
      }
      resolve(cache.get(category))
    }).catch(e => reject(e))
  })
}

function TagSelectInput(props) {
  const {
    useTagRoute = true,
    onChange,
    readOnly = false,
    category,
    options,
    isInline,
    includeSystemTags = false,
    label = null,
    placeholder = lang.tag.placeholder,
    noOptionsMessage = () => lang.tag.enterTagName,
    formatCreateLabel = lang.tag.createLabel,
    tagEditAllowed,
    dataQsId,
    showColorBubble = false,
    ...rest
  } = props

  const [isLoading, setIsLoading] = useState(false)
  const [tagOptions, setTagOptions] = useState(options || cache.get(category))
  const [value, setValue] = useState(props.value ?? [])

  useEffect(() => {
    setValue(props.value)
  }, [props.value])

  useEffect(() => {
    if (category === TAG_CATEGORY.CUSTOM) {
      return
    }

    let isUnmounted = false
    setIsLoading(true)

    if (useTagRoute === true) {
      loadTags(category)
        .then(tags => !isUnmounted && setTagOptions(includeSystemTags ? tags : tags.filter(tag => tag.isSystem === false)))
        .finally(() => !isUnmounted && setIsLoading(false))
    } else {
      setTagOptions(value)
      setIsLoading(false)
    }

    return () => {
      isUnmounted = true
    }
  }, [])

  useEffect(() => {
    if (tagOptions?.length > 0) {
      const tags = value ? cloneDeep(value) : []
      tags.forEach((tag) => {
        const foundOption = tagOptions.find(option => option.id === tag.id)
        if (foundOption) {
          tag.label = foundOption.label
          tag.color = foundOption.color || null
          tag.backgroundColor = foundOption.backgroundColor || null
          tag.isSystem = foundOption.isSystem || null
          tag.isAssignable = foundOption.isAssignable || null
          tag.tagType = foundOption.tagType || TAG_TYPES.CUSTOM
        }
      })
      setValue(tags)
    }
  }, [tagOptions])

  /**
   * defines if tags can be removed by the user
   * @param {object} option - can only be an object with id and label
   * @returns boolean
   */
  const getOptionFixed = (option) => {
    if (isAssignableSystemTag(option.tagType)) return false
  }

  const filteredTagOptions = useMemo(() => {
    const allowToAdd = (item) => (item.tagType === REMOVABLE_SYSTEM_TAGS.NOT_ASSIGNED.type || isAssignableSystemTag(item.tagType))
    return tagOptions?.filter(allowToAdd)
  }, [tagOptions])

  const { sortable, styles: externalStyles } = props

  const getNewOptionData = (inputValue, optionLabel) => {
    return {
      id: `new_${inputValue}`,
      label: optionLabel,
      tagType: TAG_TYPES.CUSTOM,
      isAssignable: true,
      backgroundColor: theme.newTagColor,
      __isNew__: true,
    }
  }

  return (
    <MultiSelectInput
      {...rest}
      value={value}
      label={label}
      showColorBubble={showColorBubble}
      placeholder={placeholder}
      alwaysShowPlaceholder={true}
      readOnly={readOnly}
      allowCreate={tagEditAllowed}
      isLoading={isLoading}
      options={filteredTagOptions}
      isClearable={false}
      formatCreateLabel={formatCreateLabel}
      noOptionsMessage={noOptionsMessage}
      getNewOptionData={getNewOptionData}
      getOptionValue={(option) => option.id}
      getOptionFixed={getOptionFixed}
      onValueChange={onChange}
      isInline={isInline}
      dataQsId={dataQsId}
      styles={{
        ...externalStyles,
        multiValue: (styles, props) => {
          const newStyle = {
            ...styles,
            zIndex: sortable ? null : 'auto',
          }
          return externalStyles?.multiValue ? externalStyles.multiValue(newStyle, props) : newStyle
        },
      }}
    />
  )
}

TagSelectInput.propTypes = {
  value: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape(PropTypes.object),
      PropTypes.shape(PropTypes.string),
      PropTypes.shape(PropTypes.null),
    ]),
  ),
  onChange: PropTypes.func,
  category: PropTypes.oneOf(Object.keys(TAG_CATEGORY).map(category => TAG_CATEGORY[category])).isRequired,
  options: PropTypes.array, // category should be 'custom'
  readOnly: PropTypes.bool,
  isInline: PropTypes.bool,
  includeSystemTags: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  noOptionsMessage: PropTypes.func,
  useTagRoute: PropTypes.bool,
  useFormik: PropTypes.bool,
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  dataQsId: PropTypes.string.isRequired,
}

export {
  TagSelectInput,
  ACTION_TYPES,
}

export default TagSelectInput
